import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaChevronLeft, FaChevronRight, FaPlus, FaTrash, FaImage } from 'react-icons/fa';
import { useApp, useAutoRefresh, triggerRefresh } from '../context/AppContext';
import { getBanners, createBanner, deleteBanner, getFileUrl } from '../services/api';

const BannerSlider = () => {
  const { user, showToast } = useApp();
  const role = user?.role || '';
  const [banners, setBanners] = useState([]);
  const [index, setIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchBanners = useCallback(async () => {
    try {
      const res = await getBanners();
      setBanners(res.data?.data || []);
    } catch { setBanners([]); }
  }, []);

  useEffect(() => { fetchBanners(); }, [fetchBanners]);
  useAutoRefresh(fetchBanners, 'banners');

  useEffect(() => {
    if (banners.length < 2) return;
    const t = setInterval(() => setIndex(i => (i + 1) % banners.length), 4000);
    return () => clearInterval(t);
  }, [banners.length]);

  useEffect(() => { if (index >= banners.length) setIndex(0); }, [banners.length, index]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('images', file));
      await createBanner(formData);
      showToast(files.length > 1 ? `${files.length} banners added!` : 'Banner added!', 'success');
      triggerRefresh('banners');
    } catch (err) {
      showToast(err.message || 'Failed to upload banner', 'error');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this banner?')) return;
    try {
      await deleteBanner(id);
      showToast('Banner removed', 'success');
      triggerRefresh('banners');
    } catch (err) { showToast(err.message || 'Delete failed', 'error'); }
  };

  if (banners.length === 0 && role !== 'Admin') return null;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
      {banners.length > 0 ? (
        <div style={{ position: 'relative', width: '100%', background: 'var(--primary-bg)' }}>
          <img
            src={getFileUrl(banners[index]?.imageUrl)}
            alt={banners[index]?.title || 'Banner'}
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
          {banners.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => setIndex(i => (i - 1 + banners.length) % banners.length)}
                aria-label="Previous banner"
                style={{
                  position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)',
                  width: 44, height: 44, minWidth: 44, minHeight: 44,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'transparent', border: 'none', outline: 'none',
                  color: '#fff', fontSize: 20, cursor: 'pointer',
                  filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))',
                  WebkitTapHighlightColor: 'transparent',
                }}
              ><FaChevronLeft /></button>
              <button
                type="button"
                onClick={() => setIndex(i => (i + 1) % banners.length)}
                aria-label="Next banner"
                style={{
                  position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                  width: 44, height: 44, minWidth: 44, minHeight: 44,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'transparent', border: 'none', outline: 'none',
                  color: '#fff', fontSize: 20, cursor: 'pointer',
                  filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))',
                  WebkitTapHighlightColor: 'transparent',
                }}
              ><FaChevronRight /></button>
              <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 6 }}>
                {banners.map((b, i) => (
                  <span
                    key={b.id}
                    onClick={() => setIndex(i)}
                    style={{ width: 8, height: 8, borderRadius: '50%', cursor: 'pointer', background: i === index ? 'var(--primary)' : 'rgba(255,255,255,0.7)', boxShadow: '0 0 0 1px rgba(0,0,0,0.15)' }}
                  />
                ))}
              </div>
            </>
          )}
          {role === 'Admin' && (
            <button type="button" onClick={() => handleDelete(banners[index].id)} title="Remove this banner" className="action-btn action-btn-delete" style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(255,255,255,0.9)' }}><FaTrash /></button>
          )}
        </div>
      ) : (
        <div style={{ padding: 24, textAlign: 'center', color: '#6b7a8d' }}>
          <FaImage style={{ fontSize: 28, marginBottom: 8 }} />
          <p style={{ margin: 0, fontSize: 13 }}>No banners yet — add one to show a slider here.</p>
        </div>
      )}

      {role === 'Admin' && (
        <div style={{ padding: '10px 14px', borderTop: banners.length ? '1px solid var(--border)' : 'none', display: 'flex', justifyContent: 'flex-end' }}>
          <label className="btn btn-outline" style={{ margin: 0, cursor: uploading ? 'default' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
            <FaPlus /> {uploading ? 'Uploading...' : 'Add Banners'}
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleUpload} disabled={uploading} style={{ display: 'none' }} />
          </label>
        </div>
      )}
    </div>
  );
};

export default BannerSlider;
